import { Arr } from '@deqo/foundation';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import { LFC } from '../../Legacy';
import { Fill } from '../../ui/Fill';
import { useResolve } from '../Container';
import { Wrapper } from '../Wrapper';
import { Popup } from './Popup';
import { PopupManager } from './PopupManager';

const popupAnimation = (fullscreen: boolean): Variants => ({
    before: {
        opacity: 0,
        scale: .75
    },
    during: {
        opacity: 1,
        scale: 1.0,
        transition: {
            opacity: { duration: .25, ease: 'linear', },
            scale: { duration: .35, ease: [.16, 1.17, .47, 1.01], },
        }
    },
    after: fullscreen ? (
        {
            opacity: 0,
            scale: .75,
            transition: {
                ease: [.16, 1.17, .47, 1.01],
                duration: .25,
            }
        }
    ) : (
        {
            opacity: 0,
            y: '-5rem',
            transition: {
                ease: 'easeOut',
                duration: .2,
            }
        }
    )
});

const AnimationContainer = styled(Fill)`
    pointer-events: none;
    & > div {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: all;
    }
`;

const PopupPortal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 100;
`;

const PopupMount: LFC = ({ children }) => <>
    {ReactDOM.createPortal(<PopupPortal id="popup-layer">{children}</PopupPortal>, document.body)}
</>;


const PopupFade = styled.div<{ active?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: opacity 200ms ease-out;
    background: black;
    ${p => p.active ? css`
        opacity: 0.3;
        pointer-events: none;
    ` : css`
        opacity: 0;
    `}
`;

const PopupFrame = styled.div<{ fullscreen: boolean }>`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
    cursor: default;

    ${p => !p.fullscreen ? '' : css`
        ${Popup.Base} {
            width: 100%;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            flex: 1 1 auto;
            border-radius: 0;
        }
    `}
`;

export const PopupSupport = () => {

    const wrapper = useResolve(Wrapper);
    const popups = useResolve(PopupManager);

    const [buffer, setBuffer] = useState([popups.current].filter(x => !!x));
    const current = Arr.last(buffer);
    const fade = !!buffer.length && !buffer.find(x => x.fade === false);

    useEffect(() => popups.observe(ns => setBuffer([ns.current].filter(x => !!x))), []);
    useEffect(() => wrapper.setEffect(current && current.fullscreen ? 'drop' : 'none'), [current]);
    useEffect(() => () => wrapper.setEffect('none'), []);

    return <PopupMount>
        <PopupFade active={fade} />
        <AnimationContainer>
            <AnimatePresence>
                {buffer.map(({ id, fullscreen, ui, action, dismiss, args }) => (
                    <motion.div key={id} variants={popupAnimation(fullscreen)} initial="before" animate="during" exit="after">
                        <PopupFrame fullscreen={fullscreen}>
                            {React.createElement(ui, { action, dismiss, args } as any)}
                        </PopupFrame>
                    </motion.div>
                ))}
            </AnimatePresence>
        </AnimationContainer>
    </PopupMount>;
};
